import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  agentPromptVersionKey,
  agentPromptVersionsApi,
  agentPromptVersionsKey,
} from "@/core/api/agentPromptVersionsApi";
import { useAuth } from "@/core/hooks/useAuth";
import { baseQueryOptions } from "@/core/hooks/useApiQueries/baseQueryOptions";
import { useUserSector } from "@/core/hooks/useUserSector";
import { dedupeByDocumentId } from "@/shared/utils/dedupeByDocumentId";

/**
 * Versões do prompt do setor ativo (SA-1486), paginadas por scroll infinito; só busca com o histórico aberto.
 */
export function useAgentPromptVersions(enabled: boolean) {
  const { token } = useAuth();
  const sectorId = useUserSector().activeSectorId ?? "";
  const query = useInfiniteQuery({
    ...baseQueryOptions,
    queryKey: agentPromptVersionsKey(sectorId),
    queryFn: ({ pageParam }) =>
      agentPromptVersionsApi.list(token, sectorId, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    enabled: enabled && Boolean(sectorId),
  });
  const pages = query.data?.pages;
  return {
    // Offset: uma versão salva em outra aba desloca as páginas seguintes.
    data: pages && dedupeByDocumentId(pages.flatMap((page) => page.items)),
    total: pages?.[0]?.total ?? 0,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Texto de uma versão e o da anterior: imutável, não relê. */
export function useAgentPromptVersion(versionId: string | null) {
  const { token } = useAuth();
  const sectorId = useUserSector().activeSectorId ?? "";
  return useQuery({
    ...baseQueryOptions,
    queryKey: agentPromptVersionKey(sectorId, versionId ?? ""),
    queryFn: () => agentPromptVersionsApi.get(token, sectorId, versionId!),
    enabled: Boolean(versionId) && Boolean(sectorId),
    staleTime: Infinity,
  });
}
